'use client';

import { SessionData } from '@auth0/nextjs-auth0/types';
import posthog from 'posthog-js';
import { useEffect, useRef } from 'react';
import { differenceInMonths, differenceInYears } from 'date-fns';
import { useAppSelector } from 'lib/hooks';
import { RootState } from 'lib/store';
import { getEnrollmentWindow, getIEPStatus } from 'lib/eligibilityFormulas';
import { normalizePartnerCode } from 'lib/partnerCode';
import {
  authMethodFromSub,
  captureAuthSuccess,
  clearAuthAnalyticsSession,
} from 'lib/analytics';

interface PostHogIdentifierProps {
  session: SessionData | null;
  /**
   * First-touch partner code known to the server (the `mah_partner_source`
   * cookie, or the member's stored acquisition code). The query param is gone
   * by the time a visitor returns from Auth0, so the client cannot rely on it.
   */
  partnerSource?: string | null;
}

function resolvePartnerSource(serverPartnerSource: string | null) {
  const fromQuery = normalizePartnerCode(
    new URLSearchParams(window.location.search).get('partner')
  );
  const fromServer = normalizePartnerCode(serverPartnerSource);
  const alreadyRegistered = normalizePartnerCode(
    posthog.get_property('partner_source') as string | undefined
  );
  // Server cookie / acquisition code and the already-registered super
  // property are first-touch. The query param is only the source on the
  // first request, before the cookie exists on the incoming request.
  return fromServer ?? alreadyRegistered ?? fromQuery;
}

function identifySessionUser(
  session: SessionData,
  partnerSource: string | null,
  identifiedUserRef: { current: string | null },
  identifiedPartnerRef: { current: string | null }
) {
  const userEmail = session.user.email;
  const userName = session.user.name;
  const userId = session.user.sub;
  const partner = resolvePartnerSource(partnerSource);

  const isNewUser = identifiedUserRef.current !== userEmail;
  const isNewPartner = !!partner && identifiedPartnerRef.current !== partner;

  if (!userEmail || (!isNewUser && !isNewPartner)) return;

  // Super properties only ride along on events, so the partner has to be
  // sent through identify to land on the person. $set_once keeps it
  // first-touch.
  posthog.identify(
    userEmail,
    {
      email: userEmail,
      name: userName,
      auth0_id: userId,
    },
    partner ? { partner_source: partner } : undefined
  );
  if (isNewUser) {
    captureAuthSuccess(authMethodFromSub(session.user.sub));
  }
  identifiedUserRef.current = userEmail;
  identifiedPartnerRef.current = partner;
}

function getAgeBand(birthdate: string): string {
  const birth = new Date(birthdate);
  const now = new Date();
  const years = differenceInYears(now, birth);
  const months = differenceInMonths(now, birth) % 12;
  return `${years}y ${months}m`;
}

function getIncomeTier(income: number, fileTaxes: string): string {
  if (fileTaxes === 'jointly') {
    if (income <= 218000) return 'joint_under_218k';
    if (income <= 274000) return 'joint_218k_to_274k';
    if (income <= 342000) return 'joint_274k_to_342k';
    if (income <= 410000) return 'joint_342k_to_410k';
    if (income <= 750000) return 'joint_410k_to_750k';
    return 'joint_over_750k';
  }
  if (income <= 109000) return 'individual_under_109k';
  if (income <= 137000) return 'individual_109k_to_137k';
  if (income <= 171000) return 'individual_137k_to_171k';
  if (income <= 205000) return 'individual_171k_to_205k';
  if (income <= 500000) return 'individual_205k_to_500k';
  return 'individual_over_500k';
}

function getEnrollmentPeriod(birthdate: string, hasSEP: boolean): string {
  const iepStatus = getIEPStatus(new Date(birthdate));
  if (iepStatus === 'duringIEP') return 'IEP';
  if (hasSEP) return 'SEP';
  return getEnrollmentWindow(new Date());
}

export default function PostHogIdentifier({
  session,
  partnerSource = null,
}: PostHogIdentifierProps): null {
  const identifiedUserRef = useRef<string | null>(null);
  const identifiedPartnerRef = useRef<string | null>(null);

  const eligibilityValues = useAppSelector(
    (state: RootState) => state.eligibility.values
  );
  const sep = useAppSelector((state: RootState) => state.eligibility.sep);
  const zipCode = useAppSelector((state: RootState) => state.location.zipcode);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    if (session?.user) {
      identifySessionUser(
        session,
        partnerSource,
        identifiedUserRef,
        identifiedPartnerRef
      );
    } else if (identifiedUserRef.current !== null) {
      // User signed out - reset to anonymous
      posthog.reset();
      clearAuthAnalyticsSession();
      identifiedUserRef.current = null;
      identifiedPartnerRef.current = null;
    }

    const partner = resolvePartnerSource(partnerSource);
    if (partner) {
      // First-touch attribution: never overwrite an already-captured value.
      posthog.register_once({ partner_source: partner });
    }

    // Anonymous visitors still get a person profile so partner can be
    // queried in PostHog before signup. $set_once keeps first-touch.
    if (!session?.user && partner && identifiedPartnerRef.current !== partner) {
      posthog.setPersonProperties(undefined, { partner_source: partner });
      identifiedPartnerRef.current = partner;
    }
  }, [session, partnerSource]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const properties: Record<string, string | boolean> = {};

    const birthdate = eligibilityValues.birthdate;
    if (birthdate) {
      properties.age_band = getAgeBand(birthdate);
      properties.enrollment_period = getEnrollmentPeriod(
        birthdate,
        sep.isEligible
      );
    }
    if (eligibilityValues.gender) {
      properties.gender = eligibilityValues.gender;
    }
    if (eligibilityValues.currentlyMarried) {
      properties.marital_status =
        eligibilityValues.currentlyMarried === 'yes' ? 'married' : 'single';
    }
    const zip = zipCode || eligibilityValues.zipCode;
    if (zip) {
      properties.zip_code = zip;
    }
    if (eligibilityValues.income) {
      properties.income_tier = getIncomeTier(
        eligibilityValues.income,
        eligibilityValues.fileTaxes
      );
    }
    if (eligibilityValues.currentInsuranceQ) {
      properties.other_coverage = eligibilityValues.currentInsuranceQ;
    }
    if (eligibilityValues.currentInsuranceExpQ) {
      properties.creditable_coverage = eligibilityValues.currentInsuranceExpQ;
    }

    if (Object.keys(properties).length > 0) {
      posthog.setPersonProperties(properties);
    }
  }, [eligibilityValues, sep, zipCode]);

  return null;
}
